import Link from 'next/link';
import styled from 'styled-components';

import { ApiOrganizationPicked } from '~/api/v2/searches/organizations/get';
import * as COLORS from '~/colors';
import { SusnetImage } from '~/components/_wrapper/SusnetImage';

type Props = {
  spo: ApiOrganizationPicked;
};

export function CardSpo({ spo }: Props) {
  const color = COLORS.sdgs[spo.sdgs[0]] || { start: '#fff', end: 'fff' };

  return (
    <StyledWrapper href={`/orgs/${spo.uid}`}>
      <StyledCardHead>
        <StyledCardHeadImage
          src={spo.cover ?? '/images/spo_placeholder.jpg'}
          alt='spo-cover'
          fill
          sizes='100vw'
        />
        {spo.logo && (
          <StyledCardHeadLogo src={spo.logo} alt='spo-logo' width={85} height={85} sizes='100vw' />
        )}
      </StyledCardHead>
      <StyledCardBody>
        <StyledCardBodyTop>
          <StyledCardBodyTopLabel>{spo.area}</StyledCardBodyTopLabel>
          <StyledCardBodyTopName>{spo.category}</StyledCardBodyTopName>
        </StyledCardBodyTop>
        <StyledCardBodyBottom>
          <StyledCardBodyBottomTitle>{spo.name}</StyledCardBodyBottomTitle>
          <StyledCardBodyBottomBorder />
          <StyledCardBodyBottomHashtagList>
            {spo.tags.map((tag, index) => (
              <StyledCardBodyBottomHashtagListItem key={index}>
                #{tag.name}
              </StyledCardBodyBottomHashtagListItem>
            ))}
          </StyledCardBodyBottomHashtagList>
        </StyledCardBodyBottom>
        <StyledBackground start={color.start} end={color.end} />
      </StyledCardBody>
    </StyledWrapper>
  );
}

const StyledWrapper = styled(Link)`
  border-radius: 0 12px 12px 12px;

  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 320px;
`;

const StyledCardHead = styled.div`
  background-color: #ffffff;
  border-radius: 0 12px 0 0;
  height: 160px;
  position: relative;
  width: 100%;
`;

const StyledCardHeadImage = styled(SusnetImage)`
  border-radius: 0 12px 0 0;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const StyledCardHeadLogo = styled(SusnetImage)`
  aspect-ratio: 1 / 1;
  background-color: #fff;
  left: 0;
  object-fit: contain;
  position: absolute;
  top: 0;
`;

const StyledCardBody = styled.div`
  background: #fff;
  position: relative;
  border-radius: 0 0 12px 12px;
  width: 100%;
  z-index: 0;
`;

const StyledBackground = styled.div<{ start: string; end: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, ${(props) => props.end}, ${(props) => props.start});
  opacity: 0.5;
  border-radius: inherit;
  z-index: -1;
`;

const StyledCardBodyTop = styled.div`
  display: flex;
  width: 100%;
`;

const StyledCardBodyTopLabel = styled.p`
  background-color: #000;
  color: #ffffff;
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  overflow: hidden;
  padding: 8px 12px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 105px;
  max-width: 150px;
`;

const StyledCardBodyTopName = styled.p`
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.3;
  overflow: hidden;
  padding: 8px 12px;
  text-align: left;
  width: 100%;
`;

const StyledCardBodyBottom = styled.div`
  padding: 16px 32px;
  width: 100%;
`;

const StyledCardBodyBottomTitle = styled.h3`
  display: -webkit-box;
  font-size: 20px;
  font-weight: bold;
  min-height: 90px;
  line-height: 1.5;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  width: 100%;
  word-wrap: break-word;
`;

const StyledCardBodyBottomBorder = styled.hr`
  border-top: 1px solid #070707;
  margin: 16px 0;
  width: 100%;
`;

const StyledCardBodyBottomHashtagList = styled.ul`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  width: 100%;
`;

const StyledCardBodyBottomHashtagListItem = styled.li`
  color: #070707;
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.5;
  margin-right: 16px;
`;
